import React, { useContext, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Check, Copy, Eye, EyeSlash } from "react-bootstrap-icons";

import { useParams } from "react-router-dom";
import { BrowseContext } from "../../../contexts/BrowseContext";
import axiosService from "../../../utils/AxiosService";
import Modal from "../../Modal/Modal";
import Input from "../../FormComponents/Input";
import { toastSuccess, toastError } from "../../FormComponents/Toasts";

const germanShareNote =
  "Der Zugriff auf die Kundeninformationen erfolgt über einen geschützten Link. Bitte stelle sicher, dass die Zugangsdaten ausschließlich berechtigten Personen zugänglich sind und nicht missbräuchlich oder für andere Zwecke verwendet werden. Alle einsehbaren Informationen sind vertraulich zu behandeln. Kommentare, die über diese Funktion eingetragen werden, sind für alle Zugangsberechtigten sichtbar. Wir bitten daher, auf die Eingabe sensibler Daten zu verzichten.";
const englishShareNote =
  "Access to the client information is provided via a protected link. Please ensure that the login details are only accessible to authorized individuals and are not misused or used for other purposes. All accessible information must be treated as confidential. Comments entered through this function are visible to all authorized users. We therefore ask you to refrain from entering sensitive data.";

const buildShareLinkCopyText = (url, language) => {
  const note = language === "de" ? germanShareNote : englishShareNote;
  return `${note}\n\n${url}`;
};

export default observer(function ClientShareModal() {
  const { browseState, openShareClient, clientCampaigns } =
    useContext(BrowseContext);
  const { client_id } = useParams();

  const [language, setLanguage] = useState("en");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState([]);
  const [savedState, setSavedState] = useState(null);
  const [existingShareLinkId, setExistingShareLinkId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!openShareClient || !client_id) return;

    axiosService
      .get(`/api/project_manager/clients/${client_id}/client_share_link`)
      .then((shareLink) => {
        if (!shareLink) return;
        setExistingShareLinkId(shareLink.id);
        setLanguage(shareLink.language);
        setPassword(shareLink.password);
        setSelectedCampaignIds(shareLink.campaign_ids || []);
        setSavedState({
          language: shareLink.language,
          password: shareLink.password,
          campaignIds: shareLink.campaign_ids || [],
        });
      })
      .catch(() => {});
  }, [openShareClient, client_id]);

  const onClose = () => {
    browseState.toggleShareClient();
    setPassword("");
    setLanguage("en");
    setSelectedCampaignIds([]);
    setSavedState(null);
    setExistingShareLinkId(null);
    setShowPassword(false);
  };

  const toggleCampaign = (campaignId) => {
    setSelectedCampaignIds((prev) =>
      prev.includes(campaignId)
        ? prev.filter((id) => id !== campaignId)
        : [...prev, campaignId],
    );
  };

  const saveAndCopyLink = () => {
    if (!password.trim()) {
      toastError("Password is required");
      return;
    }

    setIsSaving(true);
    axiosService
      .post(`/api/project_manager/clients/${client_id}/client_share_link`, {
        client_share_link: {
          password,
          language,
          campaign_ids: selectedCampaignIds,
        },
      })
      .then((shareLink) => {
        setExistingShareLinkId(shareLink.id);
        setSavedState({ language, password, campaignIds: selectedCampaignIds });
        const url = `${window.location.origin}/advertiser/clients/${shareLink.token}`;
        navigator.clipboard.writeText(buildShareLinkCopyText(url, language));
        toastSuccess("Link copied to clipboard");
      })
      .catch(({ data }) => toastError(data?.error || "Something went wrong"))
      .finally(() => setIsSaving(false));
  };

  const revokeLink = () => {
    axiosService
      .delete(`/api/project_manager/clients/${client_id}/client_share_link`)
      .then(() => {
        setExistingShareLinkId(null);
        setPassword("");
        setSelectedCampaignIds([]);
        toastSuccess("Share link revoked");
      })
      .catch(({ data }) => toastError(data?.error || "Something went wrong"));
  };

  const hasUnsavedChanges =
    savedState !== null &&
    (language !== savedState.language ||
      password !== savedState.password ||
      selectedCampaignIds.length !== savedState.campaignIds.length ||
      selectedCampaignIds.some((id) => !savedState.campaignIds.includes(id)));

  const getCampaignState = (campaign) => {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const toDateStr = (val) => {
      if (!val) return null;
      // Oj :ruby time format returns an object with a .utc string property
      const raw = val && typeof val === "object" ? val.utc : String(val);
      if (!raw) return null;
      return raw.split("T")[0].split(" ")[0];
    };
    const start = toDateStr(campaign.start_date);
    const end = toDateStr(campaign.end_date);
    if (start && today < start)
      return { label: "Not started", dot: "bg-yellow-200" };
    if (end && today > end) return { label: "Finished", dot: "bg-gray-400" };
    return { label: "Active", dot: "bg-rb-green" };
  };

  return (
    <Modal isOpen={openShareClient} onClose={onClose}>
      <Modal.Header>Share</Modal.Header>
      <Modal.Body>
        <div className="w-[480px] flex flex-col gap-5">
          <div>
            <p className="text-sm font-semibold mb-2 dark:text-white">
              Language
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setLanguage("en")}
                className={`flex items-center justify-between px-4 py-3 rounded-lg border-1 font-medium text-sm transition-colors ${
                  language === "en"
                    ? "bg-rb-green text-white border-rb-green"
                    : "bg-white dark:bg-rb-dark-mode border-rb-lite-grey dark:border-rb-dark-mode-lighter text-gray-700 dark:text-white"
                }`}
              >
                English
                {language === "en" && <Check size={24} />}
              </button>
              <button
                type="button"
                onClick={() => setLanguage("de")}
                className={`flex items-center justify-between px-4 py-3 rounded-lg border-1 font-medium text-sm transition-colors ${
                  language === "de"
                    ? "bg-rb-green text-white border-rb-green"
                    : "bg-white dark:bg-rb-dark-mode border-rb-lite-grey dark:border-rb-dark-mode-lighter text-gray-700 dark:text-white"
                }`}
              >
                Deutsch
                {language === "de" && <Check size={24} />}
              </button>
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold mb-1 dark:text-white">
              Password
            </p>
            <Input
              topMargin="mt-0"
              placeholder="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              addon={
                showPassword ? (
                  <EyeSlash className="cursor-pointer" />
                ) : (
                  <Eye className="cursor-pointer" />
                )
              }
              addonPosition="end"
              onAddonClick={() => setShowPassword((prev) => !prev)}
            />
          </div>

          <div>
            <p className="text-sm font-semibold mb-2 dark:text-white">
              Campaigns
            </p>
            <div className="max-h-64 overflow-y-auto flex flex-col border-1 border-rb-lite-grey dark:border-rb-dark-mode-lighter rounded-lg divide-y divide-rb-lite-grey dark:divide-rb-dark-mode-lighter">
              {clientCampaigns.length === 0 && (
                <p className="text-sm text-gray-400 px-4 py-3">
                  No campaigns found
                </p>
              )}
              {clientCampaigns.map((campaign) => (
                <div
                  key={campaign.id}
                  className="flex items-center gap-2 px-4 py-3 hover:bg-rb-grey dark:hover:bg-rb-dark-mode-lighter cursor-pointer"
                  onClick={() => toggleCampaign(campaign.id)}
                >
                  <div
                    className={`w-4 h-4 rounded border-1 flex-shrink-0 flex items-center justify-center transition-colors ${
                      selectedCampaignIds.includes(campaign.id)
                        ? "bg-rb-green border-rb-green"
                        : "border-gray-300 dark:border-rb-dark-mode-lighter bg-white dark:bg-rb-dark-mode"
                    }`}
                  >
                    {selectedCampaignIds.includes(campaign.id) && (
                      <Check className="text-white" style={{ fontSize: 10 }} />
                    )}
                  </div>

                  <span
                    className="size-4 rounded-[4px] flex items-center justify-center text-white text-[8px] font-bold flex-shrink-0"
                    style={{
                      backgroundColor: campaign.theme_color || "#6b7280",
                    }}
                  >
                    {campaign.name.slice(0, 2).toUpperCase()}
                  </span>

                  <span className="flex-1 min-w-0 text-sm dark:text-white truncate">
                    {campaign.name}
                  </span>

                  {(() => {
                    const { label, dot } = getCampaignState(campaign);
                    return (
                      <span className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                        <span
                          className={`w-2 h-2 rounded-full inline-block flex-shrink-0 ${dot}`}
                        />
                        {label}
                      </span>
                    );
                  })()}
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex items-center justify-between w-full">
          <div className="flex gap-2">
            <Modal.DissmissButton className="rb-btn-cancel px-4 py-2 text-sm font-semibold rounded-lg">
              Cancel
            </Modal.DissmissButton>
            {existingShareLinkId && (
              <button
                type="button"
                onClick={revokeLink}
                className="px-4 py-2 text-sm font-semibold text-red-500 hover:bg-red-50 dark:hover:bg-rb-dark-mode-lighter rounded-lg"
              >
                Revoke
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={saveAndCopyLink}
            disabled={isSaving}
            className="flex items-center gap-2 bg-rb-green text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-opacity-90 disabled:opacity-50"
          >
            <Copy />
            {hasUnsavedChanges ? "Update and copy link" : "Copy Link"}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
});
